import {
  Category,
  type GamesFilter,
  GamesFilterCategoriesStrategy,
  GamesFilterSortDirection,
  GamesFilterSortType,
  type GameV1,
} from '@/api/games';
import { mapGameV2ToGameV1 } from '@/helpers/game';
import { fetcherSSV2 } from '@/modules/api/fetcher';

export const WAGERING_HEAD_SIZE = 21;

export const POPULARITY_PAGE_SIZE = 100;

export const PREFETCH_PAGES = 2;

type Device = GamesFilter.Device;
export type Currencies = GamesFilter.Body['currencies'];

export type WageringHeadKey = readonly [
  'wagering-head',
  Device,
  Currencies,
  boolean,
];

export type WageringPopularityKey = readonly [
  'wagering-popularity',
  Device,
  Currencies,
  boolean,
  number,
];

const request = (body: Record<string, unknown>) =>
  fetcherSSV2<GamesFilter.Response>('/games_filter', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const fetchWageringHead = (device: Device, currencies: Currencies) =>
  request({
    device,
    currencies,
    filter: { categories: { identifiers: [Category.WAGERING] } },
    page: 1,
    page_size: WAGERING_HEAD_SIZE,
  });

export const fetchWageringPopularity = (
  device: Device,
  currencies: Currencies,
  page: number
) =>
  request({
    device,
    currencies,
    filter: {
      categories: {
        identifiers: [Category.WAGERING, Category.WAGERING],
        strategy: GamesFilterCategoriesStrategy.AND,
      },
    },
    sort: {
      type: GamesFilterSortType.POPULARITY,
      direction: GamesFilterSortDirection.ASC,
    },
    page,
    page_size: POPULARITY_PAGE_SIZE,
  });

export const mergeWageringGames = (
  head: GamesFilter.Response | undefined,
  popularityPages: GamesFilter.Response[] | undefined
): GameV1[] => {
  const headGames = mapGameV2ToGameV1(head?.data ?? []);
  const placed = new Set(headGames.map(({ id }) => id));
  const tail = (popularityPages ?? [])
    .flatMap((page) => mapGameV2ToGameV1(page?.data ?? []))
    .filter(({ id }) => !placed.has(id));

  return [...headGames, ...tail];
};
